import React, { useLayoutEffect, useRef, useState } from 'react';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';

import arrowRightSVG from '@plone/volto/icons/right-key.svg';
import emailSVG from '@plone/volto/icons/email.svg';
import phoneSVG from '@plone/volto/icons/mobile.svg';
import userSVG from '@plone/volto/icons/user.svg';
import summarySVG from '@plone/volto/icons/summary.svg';
import callendarSVG from '@plone/volto/icons/calendar.svg';
import imageSVG from '@plone/volto/icons/image.svg';
import newsSVG from '@plone/volto/icons/news.svg';
import linkSVG from '@plone/volto/icons/link.svg';
import checkSVG from '@plone/volto/icons/check.svg';

import type { MultiButtonsBlockItem, MultiButtonsVariant } from './index';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';

const messages = defineMessages({
  empty: {
    id: 'Add buttons using the sidebar',
    defaultMessage: 'Adicione botões na barra lateral.',
  },
  imageButton: {
    id: 'Image button',
    defaultMessage: 'Botão com imagem',
  },
  selectImage: {
    id: 'Select an image',
    defaultMessage: 'Selecione uma imagem',
  },
});

const ICONS: Record<string, any> = {
  arrowRight: arrowRightSVG,
  phone: phoneSVG,
  user: userSVG,
  summary: summarySVG,
  callendar: callendarSVG,
  image: imageSVG,
  news: newsSVG,
  link: linkSVG,
  check: checkSVG,
  email: emailSVG,
};

const getItemLink = (item: MultiButtonsBlockItem): string | undefined => {
  const href = item.href as any;

  if (Array.isArray(href) && href.length === 1) {
    return href[0]?.['@id'];
  }

  if (href && typeof href === 'object' && href['@id']) {
    return href['@id'];
  }

  if (typeof href === 'string' && href.trim()) {
    return href.trim();
  }

  return undefined;
};

const getItemImageLink = (item: MultiButtonsBlockItem): string | undefined => {
  const image = (item as any)?.image;

  if (Array.isArray(image) && image.length === 1) {
    return image[0]?.['@id'];
  }

  if (image && typeof image === 'object' && image['@id']) {
    return image['@id'];
  }

  if (typeof image === 'string' && image.trim()) {
    return image.trim();
  }

  return undefined;
};
const resolveImageSrc = (href: string | undefined): string | undefined => {
  if (!href || typeof href !== 'string') return undefined;

  const cleanHref = href.trim();
  if (!cleanHref) return undefined;

  if (!isInternalURL(cleanHref)) return cleanHref;

  const appUrl = flattenToAppURL(cleanHref).replace(/\/+$/, '');

  if (appUrl.includes('@@images')) return appUrl;

  return `${appUrl}/@@images/image`;
};

const getItemImageObject = (item: MultiButtonsBlockItem): any => {
  const image = (item as any)?.image;
  if (Array.isArray(image) && image.length > 0) return image[0];
  if (image && typeof image === 'object') return image;
  return undefined;
};

const getAccessibleLabel = (
  intl: IntlShape,
  item: MultiButtonsBlockItem,
  fallback?: string,
): string => {
  const label = (
    item.altText ||
    item.title ||
    (item as any)?.description ||
    fallback ||
    ''
  )
    .toString()
    .trim();

  // Avoid using a raw URL as label.
  const looksLikeUrl = /^https?:\/\//i.test(label) || label.startsWith('/');
  if (label && !looksLikeUrl) return label;

  return intl.formatMessage(messages.imageButton);
};

export interface MultiButtonsDefaultViewProps {
  items: MultiButtonsBlockItem[];
  isEditMode?: boolean;
  maxColumns?: number;
  stretch?: boolean;
  variant?: MultiButtonsVariant;
  themeColor?: string;
}

const DefaultView: React.FC<MultiButtonsDefaultViewProps> = ({
  items,
  isEditMode,
  maxColumns = 0,
  stretch = false,
  variant = 'default',
  themeColor,
}) => {
  const intl = useIntl();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [rowEndIndexes, setRowEndIndexes] = useState<Set<number>>(
    () => new Set(),
  );

  const colsRaw = Number.isFinite(maxColumns)
    ? Math.max(0, Math.floor(maxColumns))
    : 0;
  const cols = items.length > 0 ? Math.min(colsRaw, items.length) : 0;
  const isStretchLayout = !!stretch && cols > 0;
  const isCardLayout = variant === 'card';

  useLayoutEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    let rafId: number | null = null;

    const computeLayout = () => {
      const colsRawInner = Number.isFinite(maxColumns)
        ? Math.max(0, Math.floor(maxColumns))
        : 0;
      const colsInner =
        items.length > 0 ? Math.min(colsRawInner, items.length) : 0;
      const isStretch = !!stretch && colsInner > 0;

      const parentWidth = Math.ceil(
        (listEl.parentElement?.getBoundingClientRect().width ??
          listEl.getBoundingClientRect().width) ||
          0,
      );

      // Reset vars each run.
      listEl.style.removeProperty('--multi-buttons-item-width');
      listEl.style.removeProperty('--multi-buttons-list-max-width');
      listEl.style.removeProperty('--multi-buttons-cols');

      if (isCardLayout) {
        listEl.style.setProperty('--multi-buttons-cols', `${colsInner || 1}`);
        listEl.style.setProperty(
          '--multi-buttons-card-columns',
          `${colsInner || 1}`,
        );
      }

      if (isStretch) {
        // Stretch mode:
        // - columns are enforced by flex-basis (100%/cols)
        // - each flex line distributes remaining space, so the last line with fewer
        //   items will render larger buttons and still fill the row.
        listEl.style.setProperty('--multi-buttons-cols', `${colsInner}`);
      } else {
        // Fit mode: all buttons use the width of the largest one.
        void listEl.getBoundingClientRect();

        const naturalItemEls = Array.from(
          listEl.querySelectorAll<HTMLElement>('.multi-buttons-block__item'),
        );
        if (!naturalItemEls.length) return;

        const naturalWidths = naturalItemEls.reduce<number[]>((acc, el) => {
          const w = Math.ceil(el.getBoundingClientRect().width);
          if (Number.isFinite(w) && w > 0) acc.push(w);
          return acc;
        }, []);

        const maxItemWidth = naturalWidths.length
          ? Math.max(...naturalWidths)
          : 0;
        let targetWidth = maxItemWidth;

        // If a column count is selected, try to make that many columns fit by
        // constraining the item width (text can wrap at spaces).
        if (colsInner > 0 && parentWidth > 0) {
          const perCol = Math.floor(parentWidth / colsInner);
          if (perCol > 0) {
            targetWidth = Math.min(targetWidth, perCol);
          }
        }

        if (parentWidth > 0) {
          targetWidth = Math.min(targetWidth, parentWidth);
        }

        if (targetWidth > 0) {
          listEl.style.setProperty(
            '--multi-buttons-item-width',
            `${targetWidth}px`,
          );
        }

        // Optional max columns: reduce list max width to wrap earlier.
        if (colsInner > 0 && parentWidth > 0 && targetWidth > 0) {
          const listMax = Math.min(parentWidth, targetWidth * colsInner);
          listEl.style.setProperty(
            '--multi-buttons-list-max-width',
            `${listMax}px`,
          );
        }
      }

      // Now that the layout vars are applied, compute which items end a row.
      const laidOutEls = Array.from(
        listEl.querySelectorAll<HTMLElement>('.multi-buttons-block__item'),
      );

      const ends = new Set<number>();
      let currentTop: number | null = null;
      let lastIndexInRow: number | null = null;

      laidOutEls.forEach((el, idx) => {
        const top = el.offsetTop;

        if (currentTop === null) {
          currentTop = top;
          lastIndexInRow = idx;
          return;
        }

        if (top !== currentTop) {
          if (lastIndexInRow !== null) ends.add(lastIndexInRow);
          currentTop = top;
        }

        lastIndexInRow = idx;
      });

      if (lastIndexInRow !== null) ends.add(lastIndexInRow);

      setRowEndIndexes((prev) => {
        if (prev.size !== ends.size) return ends;
        for (const value of ends) {
          if (!prev.has(value)) return ends;
        }
        return prev;
      });
    };

    const scheduleCompute = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(computeLayout);
    };

    scheduleCompute();

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleCompute)
        : null;
    if (ro) ro.observe(listEl);

    window.addEventListener('resize', scheduleCompute);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', scheduleCompute);
    };
  }, [items, maxColumns, stretch, isCardLayout]);

  if (!items.length) {
    if (!isEditMode) return null;
    return (
      <div className="multi-buttons-block__empty">
        {intl.formatMessage(messages.empty)}
      </div>
    );
  }

  return (
    <div
      className={`multi-buttons-block__list${
        isStretchLayout ? ' is-stretch' : ''
      }`}
      style={
        themeColor
          ? ({ '--multi-buttons-bg': themeColor } as React.CSSProperties)
          : undefined
      }
      ref={listRef}
    >
      {items.map((item, index) => {
        const href = getItemLink(item);
        const iconSVG = item.icon ? ICONS[item.icon] : undefined;
        const title = item.title || '';
        const description = (item.description || '').toString();
        const openLinkInNewTab = item.openInNewTab ?? false;

        const imageHref =
          variant === 'image' ? getItemImageLink(item) : undefined;
        const imageObj =
          variant === 'image' ? getItemImageObject(item) : undefined;
        const imageSrc =
          variant === 'image' ? resolveImageSrc(imageHref) : undefined;
        const accessibleLabel =
          variant === 'image'
            ? getAccessibleLabel(intl, item, imageHref)
            : title || undefined;

        const content =
          variant === 'card' ? (
            <div className="multi-buttons-card-content">
              {title ? (
                <h2 className="multi-buttons-card-title">{title}</h2>
              ) : null}
              {description ? (
                <p className="multi-buttons-card-description">{description}</p>
              ) : null}
            </div>
          ) : variant === 'image' ? (
            <div className="multi-buttons-image-frame">
              {imageObj && imageObj.image_scales ? (
                <Image
                  className="multi-buttons-image"
                  item={imageObj}
                  alt={accessibleLabel}
                  loading="lazy"
                />
              ) : imageSrc ? (
                <img
                  className="multi-buttons-image"
                  src={imageSrc}
                  alt={accessibleLabel}
                  loading="lazy"
                />
              ) : isEditMode ? (
                <div className="multi-buttons-image-placeholder">
                  {intl.formatMessage(messages.selectImage)}
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {iconSVG ? (
                <Icon
                  className="multi-buttons-block__icon"
                  name={iconSVG}
                  size="20px"
                />
              ) : null}
              <p className="multi-buttons-label">{title}</p>
            </>
          );

        return (
          <div
            className={`multi-buttons-block__item${
              rowEndIndexes.has(index) ? ' is-row-end' : ''
            }`}
            key={
              (item as any)._uid ||
              (item as any)['@id'] ||
              (href as string) ||
              title ||
              JSON.stringify(item)
            }
          >
            <div
              className={`multi-buttons-block__button${
                !href ? ' is-disabled' : ''
              }`}
            >
              <ConditionalLink
                condition={!isEditMode && !!href}
                href={href}
                className="multi-buttons-block-link"
                openLinkInNewTab={openLinkInNewTab}
                aria-label={accessibleLabel}
                title={accessibleLabel}
              >
                <div className="multi-buttons-block-area">{content}</div>
              </ConditionalLink>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DefaultView;
